import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

interface SidebarContextValue {
	collapsed: boolean;
	setCollapsed: (value: boolean) => void;
	pageTitle: string;
	setPageTitle: (title: string) => void;
	/**
	 * Set by a page that renders its own title and company pill, so the header
	 * drops both rather than printing the same two things 40px higher. Opt-in:
	 * every page that does not ask keeps the header title it has today.
	 */
	pageOwnsHeading: boolean;
	setPageOwnsHeading: (value: boolean) => void;
	/**
	 * EQU-799 §1. A trail the header prints in the slot the page title used to
	 * hold, for a page deep enough that "which account, which month" is the
	 * useful thing to say up there. Opt-in via `<Page breadcrumb={...}>`; a page
	 * that never supplies one is untouched.
	 */
	pageBreadcrumb: string[];
	setPageBreadcrumb: (trail: string[]) => void;
}

const EMPTY_BREADCRUMB: string[] = [];

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
	const [collapsed, setCollapsed] = useState(() => {
		try {
			return localStorage.getItem("equilityhq_sidebar_collapsed") === "true";
		} catch {
			return false;
		}
	});
	const [pageTitle, setPageTitle] = useState("");
	const [pageOwnsHeading, setPageOwnsHeading] = useState(false);
	const [pageBreadcrumb, setPageBreadcrumbState] =
		useState<string[]>(EMPTY_BREADCRUMB);

	// Idempotent on value, not on identity. A caller builds the trail inline
	// from its own render data, so it hands over a fresh array every time; a
	// plain setState would re-render the whole shell on each one.
	const setPageBreadcrumb = useCallback((trail: string[]) => {
		setPageBreadcrumbState((previous) =>
			previous.length === trail.length &&
			previous.every((segment, i) => segment === trail[i])
				? previous
				: trail,
		);
	}, []);

	const handleSetCollapsed = (value: boolean) => {
		setCollapsed(value);
		try {
			localStorage.setItem("equilityhq_sidebar_collapsed", String(value));
		} catch {
			// ignore storage errors
		}
	};

	return (
		<SidebarContext.Provider
			value={{
				collapsed,
				setCollapsed: handleSetCollapsed,
				pageTitle,
				setPageTitle,
				pageOwnsHeading,
				setPageOwnsHeading,
				pageBreadcrumb,
				setPageBreadcrumb,
			}}
		>
			{children}
		</SidebarContext.Provider>
	);
}

export function useSidebar() {
	const ctx = useContext(SidebarContext);
	if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
	return ctx;
}

export function useAutoCollapseSidebar() {
	const { collapsed, setCollapsed } = useSidebar();
	const previousCollapsed = useRef(collapsed);

	useEffect(() => {
		previousCollapsed.current = collapsed;
		setCollapsed(true);

		return () => {
			setCollapsed(previousCollapsed.current);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
}
